import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import {
  Leaf, ArrowRight, ArrowLeft, Check, Heart, Droplets, Zap,
  Moon, CalendarDays, Plus, X, Sparkles
} from 'lucide-react';

const STEPS = ['welcome', 'acne', 'body', 'lifestyle', 'food', 'complete'];

const FACE_ZONES = [
  { key: 'forehead', label: 'Forehead', style: { top: '8%', left: '50%', transform: 'translateX(-50%)', width: '70px', height: '36px', borderRadius: '35px 35px 0 0' } },
  { key: 'cheeks', label: 'Cheeks', style: { top: '38%', left: '12%', width: '45px', height: '36px', borderRadius: '22px' } },
  { key: 'cheeks_r', label: 'Cheeks', style: { top: '38%', right: '12%', width: '45px', height: '36px', borderRadius: '22px' } },
  { key: 'nose', label: 'Nose', style: { top: '33%', left: '50%', transform: 'translateX(-50%)', width: '26px', height: '40px', borderRadius: '13px' } },
  { key: 'chin', label: 'Chin', style: { bottom: '14%', left: '50%', transform: 'translateX(-50%)', width: '44px', height: '30px', borderRadius: '22px' } },
  { key: 'jawline', label: 'Jaw', style: { bottom: '24%', left: '6%', width: '36px', height: '44px', borderRadius: '18px' } },
  { key: 'jawline_r', label: 'Jaw', style: { bottom: '24%', right: '6%', width: '36px', height: '44px', borderRadius: '18px' } },
  { key: 'scalp', label: 'Scalp', style: { top: '0', left: '50%', transform: 'translateX(-50%)', width: '50px', height: '20px', borderRadius: '25px 25px 0 0' } },
];

const ProgressDots = ({ current, total }) => (
  <div className="flex gap-2 justify-center">
    {Array.from({ length: total }).map((_, i) => (
      <div key={`dot-${i}`} className={`h-2 rounded-full transition-all duration-300 ${
        i <= current ? 'bg-[#FF6B4A] w-6' : 'bg-[#E5D0C9] w-2'
      }`} />
    ))}
  </div>
);

const WelcomeStep = ({ userName, onNext }) => (
  <div className="text-center py-8 animate-fade-in" data-testid="onboarding-welcome">
    <div className="w-20 h-20 rounded-full bg-[#4ADE80]/20 flex items-center justify-center mx-auto mb-6">
      <Leaf className="w-10 h-10 text-[#FF6B4A]" />
    </div>
    <h2 className="font-heading text-3xl font-bold text-[#1A1514] mb-3">
      Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!
    </h2>
    <p className="text-[#8C7A76] text-lg mb-2 max-w-md mx-auto">
      Let's set up your skin health profile in under 2 minutes.
    </p>
    <p className="text-[#8C7A76] text-sm mb-8 max-w-sm mx-auto">
      This helps us identify patterns and give you personalized recommendations.
    </p>
    <Button onClick={onNext} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full px-8 py-6 text-lg" data-testid="onboarding-start-btn">
      Let's Go <ArrowRight className="ml-2 w-5 h-5" />
    </Button>
  </div>
);

const AcneStep = ({ acneLocations, setAcneLocations, acneSeverity, setAcneSeverity }) => (
  <div className="animate-fade-in" data-testid="onboarding-acne">
    <h3 className="font-heading text-xl font-bold text-[#1A1514] mb-1 text-center">Where are your breakouts?</h3>
    <p className="text-sm text-[#8C7A76] mb-6 text-center">Tap the areas on the face map</p>
    <div className="face-map bg-[#FFF2EC] rounded-2xl relative mx-auto mb-6" style={{ width: '180px', height: '240px' }}>
      <div className="absolute inset-[15%] border-2 border-[#E5D0C9] rounded-[50%]" />
      {FACE_ZONES.map(zone => {
        const baseKey = zone.key.replace('_r', '');
        return (
          <div key={zone.key} className={`face-zone ${acneLocations[baseKey] ? 'active' : ''}`}
            style={zone.style} onClick={() => setAcneLocations(prev => ({ ...prev, [baseKey]: !prev[baseKey] }))}
            data-testid={`onboard-zone-${zone.key}`}>
            {zone.label}
          </div>
        );
      })}
    </div>
    <div>
      <label className="text-sm font-medium text-[#1A1514] mb-2 block text-center">
        How severe? {acneSeverity[0]}/10
      </label>
      <Slider value={acneSeverity} onValueChange={setAcneSeverity} max={10} step={1} className="py-3" data-testid="onboard-acne-slider" />
      <div className="flex justify-between text-xs text-[#8C7A76]"><span>Clear</span><span>Severe</span></div>
    </div>
  </div>
);

const BodyStep = ({ bloating, setBloating, cycleDay, setCycleDay, isPeriod, setIsPeriod, trackCycle, setTrackCycle }) => (
  <div className="animate-fade-in space-y-6" data-testid="onboarding-body">
    <h3 className="font-heading text-xl font-bold text-[#1A1514] mb-1 text-center">How's your body feeling?</h3>
    <div>
      <label className="text-sm font-medium text-[#1A1514] mb-2 flex items-center gap-2">
        <Heart className="w-4 h-4 text-[#A388EE]" /> Bloating: {bloating[0]}/10
      </label>
      <Slider value={bloating} onValueChange={setBloating} max={10} step={1} className="py-2" data-testid="onboard-bloating-slider" />
      <div className="flex justify-between text-xs text-[#8C7A76]"><span>None</span><span>Very bloated</span></div>
    </div>
    <div className="p-3 bg-[#FFF2EC] rounded-xl">
      <p className="text-sm font-medium text-[#1A1514] mb-2">Do you want to track hormone cycles?</p>
      <div className="flex gap-3">
        <Button
          variant={trackCycle === true ? "default" : "outline"}
          onClick={() => setTrackCycle(true)}
          className={`flex-1 h-9 text-sm ${trackCycle === true ? "bg-[#FF6B4A] hover:bg-[#E8552F]" : "border-[#E5D0C9]"}`}
          data-testid="onboard-track-cycle-yes"
        >
          {trackCycle === true && <Check className="w-4 h-4 mr-1" />} Yes
        </Button>
        <Button
          variant={trackCycle === false ? "default" : "outline"}
          onClick={() => { setTrackCycle(false); setCycleDay(null); setIsPeriod(false); }}
          className={`flex-1 h-9 text-sm ${trackCycle === false ? "bg-[#8C7A76] hover:bg-[#6a6e68]" : "border-[#E5D0C9]"}`}
          data-testid="onboard-track-cycle-no"
        >
          {trackCycle === false && <Check className="w-4 h-4 mr-1" />} No, skip this
        </Button>
      </div>
    </div>
    {trackCycle && (
      <div className="p-3 bg-[#FFF2EC] rounded-xl animate-fade-in">
        <p className="text-xs text-[#8C7A76] mb-2">Hormone / Cycle Tracking</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[120px]">
            <Input type="number" min={1} max={35} value={cycleDay || ''}
              onChange={(e) => setCycleDay(parseInt(e.target.value) || null)}
              placeholder="Cycle day (e.g. 14)" className="border-[#E5D0C9]" data-testid="onboard-cycle-input" />
          </div>
          <Button variant={isPeriod ? "default" : "outline"} onClick={() => setIsPeriod(!isPeriod)}
            className={isPeriod ? "bg-[#A388EE] hover:bg-[#b5664a]" : "border-[#E5D0C9]"} data-testid="onboard-period-btn">
            {isPeriod && <Check className="w-4 h-4 mr-1" />} On Period
          </Button>
        </div>
      </div>
    )}
  </div>
);

const LifestyleStep = ({ water, setWater, stress, setStress, sleep, setSleep }) => (
  <div className="animate-fade-in space-y-6" data-testid="onboarding-lifestyle">
    <h3 className="font-heading text-xl font-bold text-[#1A1514] mb-1 text-center">Your daily habits</h3>
    <div>
      <label className="text-sm font-medium text-[#1A1514] mb-2 flex items-center gap-2">
        <Droplets className="w-4 h-4 text-[#FF6B4A]" /> Water today: {water[0]} glasses
      </label>
      <Slider value={water} onValueChange={setWater} max={20} step={1} className="py-2" data-testid="onboard-water-slider" />
    </div>
    <div>
      <label className="text-sm font-medium text-[#1A1514] mb-2 flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#4ADE80]" /> Stress level: {stress[0]}/10
      </label>
      <Slider value={stress} onValueChange={setStress} max={10} step={1} className="py-2" data-testid="onboard-stress-slider" />
      <div className="flex justify-between text-xs text-[#8C7A76]"><span>Calm</span><span>Very stressed</span></div>
    </div>
    <div>
      <label className="text-sm font-medium text-[#1A1514] mb-2 flex items-center gap-2">
        <Moon className="w-4 h-4 text-[#FF6B4A]" /> Sleep last night: {sleep[0]}h
      </label>
      <Slider value={sleep} onValueChange={setSleep} max={12} step={0.5} className="py-2" data-testid="onboard-sleep-slider" />
    </div>
  </div>
);

const QUICK_FOODS = ['Coffee', 'Dairy', 'Sugar', 'Gluten', 'Greens', 'Fruit', 'Water', 'Alcohol'];

const FoodStep = ({ foodIntake, setFoodIntake, foodInput, setFoodInput }) => {
  const customFoods = useMemo(() =>
    foodIntake.filter(f => !QUICK_FOODS.includes(f)),
  [foodIntake]);
  const addFood = (food) => {
    if (!foodIntake.includes(food)) setFoodIntake(prev => [...prev, food]);
  };
  const addCustom = () => {
    if (foodInput.trim() && !foodIntake.includes(foodInput.trim())) {
      setFoodIntake(prev => [...prev, foodInput.trim()]);
      setFoodInput('');
    }
  };

  return (
    <div className="animate-fade-in" data-testid="onboarding-food">
      <h3 className="font-heading text-xl font-bold text-[#1A1514] mb-1 text-center">What did you eat today?</h3>
      <p className="text-sm text-[#8C7A76] mb-4 text-center">Tap common items or type your own</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {QUICK_FOODS.map(food => (
          <button key={food} onClick={() => addFood(food)}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              foodIntake.includes(food)
                ? 'bg-[#FF6B4A] text-white'
                : 'bg-[#FFF2EC] text-[#1A1514] hover:bg-[#E5D0C9]'
            }`} data-testid={`onboard-food-${food.toLowerCase()}`}>
            {food}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <Input value={foodInput} onChange={(e) => setFoodInput(e.target.value)}
          placeholder="Add other food..." onKeyDown={(e) => e.key === 'Enter' && addCustom()}
          className="border-[#E5D0C9]" data-testid="onboard-food-input" />
        <Button onClick={addCustom} size="icon" className="bg-[#FF6B4A] hover:bg-[#E8552F]">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {customFoods.map(food => (
          <span key={food} className="food-tag">
            {food}
            <button onClick={() => setFoodIntake(prev => prev.filter(f => f !== food))}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const CompleteStep = ({ loading }) => (
  <div className="text-center py-8 animate-fade-in" data-testid="onboarding-complete">
    <div className="w-20 h-20 rounded-full bg-[#4ADE80]/20 flex items-center justify-center mx-auto mb-6">
      <Sparkles className="w-10 h-10 text-[#FF6B4A]" />
    </div>
    <h2 className="font-heading text-2xl font-bold text-[#1A1514] mb-3">You're all set!</h2>
    <p className="text-[#8C7A76] mb-2">
      {loading ? 'Saving your first log and calculating your detox score...' : 'Your first log is saved. Welcome to your skin wellness journey.'}
    </p>
  </div>
);

const OnboardingWizard = ({ userName, onComplete }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [acneLocations, setAcneLocations] = useState({ jawline: false, forehead: false, cheeks: false, chin: false, scalp: false, nose: false });
  const [acneSeverity, setAcneSeverity] = useState([0]);
  const [bloating, setBloating] = useState([0]);
  const [cycleDay, setCycleDay] = useState(null);
  const [isPeriod, setIsPeriod] = useState(false);
  const [trackCycle, setTrackCycle] = useState(null);
  const [water, setWater] = useState([0]);
  const [stress, setStress] = useState([0]);
  const [sleep, setSleep] = useState([7]);
  const [foodIntake, setFoodIntake] = useState([]);
  const [foodInput, setFoodInput] = useState('');

  const currentStepName = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const isPreLast = step === STEPS.length - 2;

  const handleNext = async () => {
    if (isPreLast) {
      // Save the log before showing complete
      setStep(step + 1);
      setLoading(true);
      await onComplete({
        acne_locations: acneLocations,
        acne_severity: acneSeverity[0],
        bloating_level: bloating[0],
        cycle_day: trackCycle ? cycleDay : null,
        is_period: trackCycle ? isPeriod : false,
        track_cycle: trackCycle === true,
        food_intake: foodIntake,
        water_glasses: water[0],
        stress_level: stress[0],
        sleep_hours: sleep[0],
      });
      setLoading(false);
    } else if (isLast) {
      return; // handled by parent timeout
    } else {
      setStep(step + 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF2EC] flex items-center justify-center px-4" data-testid="onboarding-wizard">
      <Card className="card-elevated border-0 w-full max-w-lg">
        <CardContent className="p-8">
          {!isFirst && !isLast && (
            <div className="mb-6">
              <ProgressDots current={step - 1} total={STEPS.length - 2} />
            </div>
          )}

          {currentStepName === 'welcome' && <WelcomeStep userName={userName} onNext={handleNext} />}
          {currentStepName === 'acne' && <AcneStep acneLocations={acneLocations} setAcneLocations={setAcneLocations} acneSeverity={acneSeverity} setAcneSeverity={setAcneSeverity} />}
          {currentStepName === 'body' && <BodyStep bloating={bloating} setBloating={setBloating} cycleDay={cycleDay} setCycleDay={setCycleDay} isPeriod={isPeriod} setIsPeriod={setIsPeriod} trackCycle={trackCycle} setTrackCycle={setTrackCycle} />}
          {currentStepName === 'lifestyle' && <LifestyleStep water={water} setWater={setWater} stress={stress} setStress={setStress} sleep={sleep} setSleep={setSleep} />}
          {currentStepName === 'food' && <FoodStep foodIntake={foodIntake} setFoodIntake={setFoodIntake} foodInput={foodInput} setFoodInput={setFoodInput} />}
          {currentStepName === 'complete' && <CompleteStep loading={loading} />}

          {/* Navigation buttons (hidden on welcome and complete) */}
          {!isFirst && !isLast && (
            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-[#8C7A76]" data-testid="onboard-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleNext} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full px-6" data-testid="onboard-next-btn">
                {isPreLast ? 'Save & Finish' : 'Next'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
